import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Observable, Subject, Subscription, catchError, debounceTime, distinctUntilChanged, forkJoin, of } from 'rxjs';

import { IglesiasService } from '../../core/services/iglesias.service';
import { MuelleService } from '../../core/services/muelle.service';
import { SolicitudesService } from '../../core/services/solicitudes.service';
import { UsersService } from '../../core/services/users.service';
import { UserStore } from '../../core/services/user-store.service';
import { ContentCardComponent } from '../../shared/components/content-card/content-card.component';
import { SkeletonBlockComponent } from '../../shared/components/skeleton-block/skeleton-block.component';
import {
  PersonTableActionEvent,
  PersonTableComponent,
  PersonTablePageChange,
} from '../../shared/components/person-table/person-table.component';
import { BUSQUEDA_NOMBRE_DELAY_MS } from '../../shared/helpers/busqueda-nombre-delay';
import { buildCongregacionColumns } from '../../shared/helpers/build-congregacion-columns';
import { CongregacionFiltro } from '../../shared/enums/congregacion-filtro.enum';
import { PersonTableRow } from '../../shared/interfaces/person-table.interface';
import { MuelleListResponse, MuellePersona } from '../../shared/interfaces/muelle.interface';
import { mapMuelleRow } from './map-muelle-row';

const PAGE_SIZE = 12;

const AVISO_ADMIN = 'El muelle está desactivado. Lo ves porque sos administrador.';
const AVISO_LIDER = 'El muelle está desactivado. Un administrador tiene que activarlo para ver el listado.';

@Component({
  selector: 'app-muelle',
  standalone: true,
  imports: [
    FormsModule,
    Button,
    Dialog,
    ContentCardComponent,
    PersonTableComponent,
    SkeletonBlockComponent,
    IconField,
    InputIcon,
    InputText,
    Textarea,
    Toast,
  ],
  templateUrl: './muelle.component.html',
  styleUrl: './muelle.component.scss',
})
export class MuelleComponent implements OnInit, OnDestroy {
  private readonly muelleService = inject(MuelleService);
  private readonly iglesiasService = inject(IglesiasService);
  private readonly usersService = inject(UsersService);
  private readonly solicitudes = inject(SolicitudesService);
  private readonly messages = inject(MessageService);
  readonly userStore = inject(UserStore);
  private readonly search$ = new Subject<string>();

  private loadSub?: Subscription;
  private searchSub?: Subscription;
  private contextoSub?: Subscription;

  readonly columnas = buildCongregacionColumns(CongregacionFiltro.Muelle, 1);
  readonly pageSize = PAGE_SIZE;

  readonly personas = signal<MuellePersona[]>([]);
  readonly busqueda = signal('');
  readonly esperandoBusqueda = signal(false);
  readonly page = signal(1);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly contextoListo = signal(false);
  readonly muelleActivo = signal(true);
  readonly actorSexo = signal('');
  readonly loadError = signal(false);

  readonly motivoVisible = signal(false);
  readonly motivoTexto = signal('');
  readonly personaEliminar = signal<PersonTableRow | null>(null);
  readonly enviando = signal(false);

  readonly yoId = computed(() => this.userStore.user()?._id ?? '');
  readonly rows = computed(() => this.personas().map((persona) => mapMuelleRow(persona, this.yoId())));
  readonly first = computed(() => (this.page() - 1) * PAGE_SIZE);
  readonly mostrarTabla = computed(
    () => this.contextoListo() && (this.userStore.isAdmin() || this.muelleActivo()),
  );
  readonly aviso = computed(() => {
    if (!this.contextoListo() || this.muelleActivo()) {
      return '';
    }
    return this.userStore.isAdmin() ? AVISO_ADMIN : AVISO_LIDER;
  });
  readonly subtitulo = computed(() => {
    if (this.userStore.isAdmin()) {
      return 'En este listado se encuentra cada persona que alguna vez visitó tu iglesia.';
    }
    const sexo = this.actorSexo().trim();
    const quien = sexo || 'persona';
    return `En este listado se encuentra cada ${quien} que alguna vez visitó tu iglesia.`;
  });
  readonly vacio = computed(() => {
    if (this.loadError()) {
      return 'No se pudo cargar el muelle.';
    }
    return this.busqueda()
      ? 'No hay personas que coincidan con la búsqueda.'
      : 'No hay personas sin líder.';
  });
  readonly motivoInvalido = computed(() => this.motivoTexto().trim().length === 0);

  ngOnInit(): void {
    this.searchSub = this.search$
      .pipe(debounceTime(BUSQUEDA_NOMBRE_DELAY_MS), distinctUntilChanged())
      .subscribe((value) => {
        this.esperandoBusqueda.set(false);
        this.busqueda.set(value);
        this.page.set(1);
        if (this.mostrarTabla()) {
          this.load();
        }
      });
    this.cargarContexto();
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.contextoSub?.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.esperandoBusqueda.set(value.trim() !== this.busqueda().trim());
    this.search$.next(value);
  }

  limpiarBusqueda(): void {
    const habiaTexto = this.busqueda().trim().length > 0;
    this.busqueda.set('');
    this.esperandoBusqueda.set(habiaTexto);
    this.search$.next('');
  }

  onTablePageChange(event: PersonTablePageChange): void {
    if (event.page === this.page()) {
      return;
    }
    this.page.set(event.page);
    this.load();
  }

  onMenu(event: PersonTableActionEvent): void {
    if (this.enviando()) {
      return;
    }
    switch (event.action) {
      case 'yoContacto':
        this.ejecutar(this.muelleService.contactar(event.row.id), 'Marcado como contacto.');
        break;
      case 'dejarDeContactar':
        this.ejecutar(this.muelleService.cancelarContacto(event.row.id), 'Contacto cancelado.');
        break;
      case 'aMiCelula':
        this.ejecutar(this.muelleService.agregarACelula(event.row.id), 'Agregado a tu célula.');
        break;
      case 'eliminar':
        this.personaEliminar.set(event.row);
        this.motivoTexto.set('');
        this.motivoVisible.set(true);
        break;
      default:
        break;
    }
  }

  cerrarMotivo(): void {
    if (this.enviando()) {
      return;
    }
    this.motivoVisible.set(false);
    this.personaEliminar.set(null);
    this.motivoTexto.set('');
  }

  onMotivoVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrarMotivo();
      return;
    }
    this.motivoVisible.set(true);
  }

  confirmarEliminar(): void {
    const persona = this.personaEliminar();
    const motivo = this.motivoTexto().trim();
    if (!persona || !motivo || this.enviando()) {
      return;
    }

    this.enviando.set(true);
    this.solicitudes.crearSolicitud(persona.id, 'eliminacion', motivo).subscribe({
      next: () => {
        this.enviando.set(false);
        this.cerrarMotivo();
        this.messages.add({
          severity: 'success',
          summary: 'Listo',
          detail: 'Solicitud de eliminación enviada.',
        });
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.enviando.set(false);
        this.avisar(err, 'No se pudo solicitar la eliminación.');
      },
    });
  }

  private cargarContexto(): void {
    this.loading.set(true);
    this.contextoSub = forkJoin({
      iglesia: this.iglesiasService.getCurrentIglesia(),
      yo: this.usersService.getCurrentUser().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ iglesia, yo }) => {
        this.actorSexo.set(yo?.data.sexo ?? '');
        this.muelleActivo.set(iglesia.muelle === true);
        this.contextoListo.set(true);
        if (this.userStore.isAdmin() || iglesia.muelle) {
          this.load();
          return;
        }
        this.loading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.contextoListo.set(true);
        this.loading.set(false);
        this.loadError.set(true);
        this.avisar(err, 'No se pudo cargar la iglesia.');
      },
    });
  }

  private ejecutar(request: Observable<unknown>, detail: string): void {
    this.enviando.set(true);
    request.subscribe({
      next: () => {
        this.enviando.set(false);
        this.messages.add({ severity: 'success', summary: 'Listo', detail });
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.enviando.set(false);
        this.avisar(err, 'No se pudo completar la acción.');
      },
    });
  }

  private load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.loadSub?.unsubscribe();

    const q = this.busqueda().trim();
    this.loadSub = this.muelleService
      .listar({
        page: this.page(),
        limit: PAGE_SIZE,
        q: q || undefined,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 403 && !this.userStore.isAdmin()) {
            this.muelleActivo.set(false);
            return of(listaVacia(this.page(), false, this.actorSexo()));
          }
          this.loadError.set(true);
          this.avisar(err, 'No se pudo cargar el muelle.');
          return of(listaVacia(this.page(), this.muelleActivo(), this.actorSexo()));
        }),
      )
      .subscribe({
        next: (res) => {
          if (typeof res.actorSexo === 'string') {
            this.actorSexo.set(res.actorSexo);
          }
          if (typeof res.muelleActivo === 'boolean') {
            this.muelleActivo.set(res.muelleActivo);
          }
          const total = res.pagination?.total ?? 0;
          if (total === 0 && this.page() > 1 && !this.loadError()) {
            this.page.set(this.page() - 1);
            this.load();
            return;
          }
          this.personas.set(res.data ?? []);
          this.totalRecords.set(total);
          this.loading.set(false);
        },
      });
  }

  private avisar(err: HttpErrorResponse, fallback: string): void {
    const message = err.error?.message;
    this.messages.add({
      severity: 'error',
      summary: 'Error',
      detail: typeof message === 'string' && message.trim() ? message : fallback,
    });
  }
}

function listaVacia(page: number, muelleActivo: boolean, actorSexo: string): MuelleListResponse {
  return {
    success: false,
    muelleActivo,
    actorSexo,
    data: [],
    pagination: { page, limit: PAGE_SIZE, total: 0 },
  };
}
